// Local persistence. The server only has ciphertext; decrypted history lives here only.
import type { DeviceRef, Envelope } from '../api/types'

export interface StoredSession {
  userId: string
  deviceId: string
  token: string
  /** hrth-core state snapshot (identity keys + MLS group state). */
  snapshot: Uint8Array
  /** Last processed log seq per conversation. */
  cursors: Record<string, number>
}

export interface StoredMessage {
  conv: string
  seq: number
  sender: DeviceRef
  envelope: Envelope
  /** Server receive time (ms). */
  receivedAt: number
}

export interface Storage {
  loadSession(): Promise<StoredSession | null>
  /** Atomically persists core state, cursors and newly decrypted messages. */
  save(session: StoredSession, messages: StoredMessage[]): Promise<void>
  /** Most recent `limit` messages of a conversation, oldest first. */
  messages(conv: string, limit?: number): Promise<StoredMessage[]>
  deleteConversation(conv: string): Promise<void>
  /** Removes messages from this device (Delete from device), by seq. */
  deleteMessages(conv: string, seqs: number[]): Promise<void>
  clear(): Promise<void>
  /** Small per-device settings (read markers, preferences). Never sent anywhere. */
  /** Adds messages (e.g. imported history); existing ones are kept. */
  importMessages(messages: StoredMessage[]): Promise<number>
  allMessages(): Promise<StoredMessage[]>
  getKv<T>(key: string): Promise<T | undefined>
  setKv(key: string, value: unknown): Promise<void>
}

const DB_NAME = 'hearth'
const DB_VERSION = 1

function req<T>(r: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    r.onsuccess = () => resolve(r.result)
    r.onerror = () => reject(r.error)
  })
}

function done(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve()
    tx.onerror = () => reject(tx.error)
    tx.onabort = () => reject(tx.error ?? new Error('transaction aborted'))
  })
}

export class IdbStorage implements Storage {
  private constructor(private db: IDBDatabase) {}

  static async open(name = DB_NAME): Promise<IdbStorage> {
    const open = indexedDB.open(name, DB_VERSION)
    open.onupgradeneeded = () => {
      const db = open.result
      db.createObjectStore('kv')
      const m = db.createObjectStore('messages', { keyPath: ['conv', 'seq'] })
      m.createIndex('by_conv', 'conv')
    }
    return new IdbStorage(await req(open))
  }

  async loadSession(): Promise<StoredSession | null> {
    const tx = this.db.transaction('kv', 'readonly')
    const s = (await req(tx.objectStore('kv').get('session'))) as StoredSession | undefined
    return s ?? null
  }

  async save(session: StoredSession, messages: StoredMessage[]): Promise<void> {
    const tx = this.db.transaction(['kv', 'messages'], 'readwrite')
    tx.objectStore('kv').put(session, 'session')
    const store = tx.objectStore('messages')
    for (const m of messages) store.put(m)
    await done(tx)
  }

  async messages(conv: string, limit = 500): Promise<StoredMessage[]> {
    const tx = this.db.transaction('messages', 'readonly')
    const range = IDBKeyRange.bound([conv, -Infinity], [conv, Infinity])
    const out: StoredMessage[] = []
    await new Promise<void>((resolve, reject) => {
      const cur = tx.objectStore('messages').openCursor(range, 'prev')
      cur.onsuccess = () => {
        const c = cur.result
        if (!c || out.length >= limit) return resolve()
        out.push(c.value as StoredMessage)
        c.continue()
      }
      cur.onerror = () => reject(cur.error)
    })
    return out.reverse()
  }

  async importMessages(messages: StoredMessage[]): Promise<number> {
    const tx = this.db.transaction('messages', 'readwrite')
    const store = tx.objectStore('messages')
    let added = 0
    for (const m of messages) {
      const exists = await req(store.count([m.conv, m.seq]))
      if (!exists) {
        store.put(m)
        added++
      }
    }
    await done(tx)
    return added
  }

  async allMessages(): Promise<StoredMessage[]> {
    const tx = this.db.transaction('messages', 'readonly')
    return (await req(tx.objectStore('messages').getAll())) as StoredMessage[]
  }

  async getKv<T>(key: string): Promise<T | undefined> {
    const tx = this.db.transaction('kv', 'readonly')
    return (await req(tx.objectStore('kv').get(`pref:${key}`))) as T | undefined
  }

  async setKv(key: string, value: unknown): Promise<void> {
    const tx = this.db.transaction('kv', 'readwrite')
    tx.objectStore('kv').put(value, `pref:${key}`)
    await done(tx)
  }

  async deleteConversation(conv: string): Promise<void> {
    const tx = this.db.transaction('messages', 'readwrite')
    tx.objectStore('messages').delete(IDBKeyRange.bound([conv, -Infinity], [conv, Infinity]))
    await done(tx)
  }

  async deleteMessages(conv: string, seqs: number[]): Promise<void> {
    const tx = this.db.transaction('messages', 'readwrite')
    const store = tx.objectStore('messages')
    for (const seq of seqs) store.delete([conv, seq])
    await done(tx)
  }

  async clear(): Promise<void> {
    const tx = this.db.transaction(['kv', 'messages'], 'readwrite')
    tx.objectStore('kv').clear()
    tx.objectStore('messages').clear()
    await done(tx)
  }

  close(): void {
    this.db.close()
  }
}
